import type { BrazeDateWireFormat } from '../date-field-format'
import type { BrazeAttributeSuggestedOption } from '../attribute-field-guide'
import {
  canvasLanguageFieldDef,
  registrationLanguageFieldDef,
} from '../registration-language-field'

/** Must match Brooklyn `ADMIN_QA_TRANSACTIONAL_EMAIL_CALLING_FROM_PREFIX` */
export const ADMIN_QA_CALLING_FROM_FUNCTION = 'adminAppTransactionalEmailQa'

export const TRANSACTIONAL_EMAIL_TEMPLATE_GROUP_PASSWORD = 'Password & login'
export const TRANSACTIONAL_EMAIL_TEMPLATE_GROUP_GIFT = 'Gift'
export const TRANSACTIONAL_EMAIL_TEMPLATE_GROUP_COMMERCE = 'Commerce & bonus'
export const TRANSACTIONAL_EMAIL_TEMPLATE_GROUP_SHARE = 'Share'

export type TransactionalEmailPayloadShape =
  | 'password_reset'
  | 'new_user_password_reset'
  | 'gift_recipient'
  | 'generic'

export interface TransactionalEmailTemplateDef {
  id: string
  label: string
  group: string
  payloadShape: TransactionalEmailPayloadShape
  /** `fields.event_name` for Braze (gift recipient uses gift schema; event_name set server-side) */
  eventName: string
}

export const TRANSACTIONAL_EMAIL_TEMPLATES: TransactionalEmailTemplateDef[] = [
  {
    id: 'PASSWORD_RESET',
    label: 'Password reset (PASSWORD_RESET)',
    group: TRANSACTIONAL_EMAIL_TEMPLATE_GROUP_PASSWORD,
    payloadShape: 'password_reset',
    eventName: 'PASSWORD_RESET',
  },
  {
    id: 'NEW_USER_PASSWORD_RESET',
    label: 'New user password reset (campaign)',
    group: TRANSACTIONAL_EMAIL_TEMPLATE_GROUP_PASSWORD,
    payloadShape: 'new_user_password_reset',
    eventName: 'NEW_USER_PASSWORD_RESET',
  },
  {
    id: 'LOGIN_CODE',
    label: 'Login code / magic link (LOGIN_CODE)',
    group: TRANSACTIONAL_EMAIL_TEMPLATE_GROUP_PASSWORD,
    payloadShape: 'generic',
    eventName: 'LOGIN_CODE',
  },
  {
    id: 'GIFT_SUBSCRIPTION_RECIPIENT_DELIVERY',
    label: 'Gift recipient delivery',
    group: TRANSACTIONAL_EMAIL_TEMPLATE_GROUP_GIFT,
    payloadShape: 'gift_recipient',
    eventName: 'GIFT_SUBSCRIPTION_RECIPIENT_DELIVERY',
  },
  {
    id: 'GIFT_SUBSCRIPTION_PURCHASER_RECEIPT',
    label: 'Gift purchaser receipt',
    group: TRANSACTIONAL_EMAIL_TEMPLATE_GROUP_GIFT,
    payloadShape: 'generic',
    eventName: 'GIFT_SUBSCRIPTION_PURCHASER_RECEIPT',
  },
  {
    id: 'GIFT_SUBSCRIPTION_REDEEMED',
    label: 'Gift redeemed',
    group: TRANSACTIONAL_EMAIL_TEMPLATE_GROUP_GIFT,
    payloadShape: 'generic',
    eventName: 'GIFT_SUBSCRIPTION_REDEEMED',
  },
  {
    id: 'SHARE_VIDEO_LEAD',
    label: 'Share video lead',
    group: TRANSACTIONAL_EMAIL_TEMPLATE_GROUP_SHARE,
    payloadShape: 'generic',
    eventName: 'SHARE_VIDEO_LEAD',
  },
  {
    id: 'ALACARTE_PURCHASE_MADE',
    label: 'À la carte purchase',
    group: TRANSACTIONAL_EMAIL_TEMPLATE_GROUP_COMMERCE,
    payloadShape: 'generic',
    eventName: 'ALACARTE_PURCHASE_MADE',
  },
  {
    id: 'BONUS_GRANTED',
    label: 'Bonus granted',
    group: TRANSACTIONAL_EMAIL_TEMPLATE_GROUP_COMMERCE,
    payloadShape: 'generic',
    eventName: 'BONUS_GRANTED',
  },
  {
    id: 'BONUS_SKU_SUBSCRIPTION_SIGNUP',
    label: 'Bonus SKU subscription signup',
    group: TRANSACTIONAL_EMAIL_TEMPLATE_GROUP_COMMERCE,
    payloadShape: 'generic',
    eventName: 'BONUS_SKU_SUBSCRIPTION_SIGNUP',
  },
  {
    id: 'BONUS_SKU_SUBSCRIPTION_PLAN_CHANGE',
    label: 'Bonus SKU plan change',
    group: TRANSACTIONAL_EMAIL_TEMPLATE_GROUP_COMMERCE,
    payloadShape: 'generic',
    eventName: 'BONUS_SKU_SUBSCRIPTION_PLAN_CHANGE',
  },
]

/** Custom events tab naming (same data as transactional email templates) */
export const CUSTOM_EVENT_TEMPLATES = TRANSACTIONAL_EMAIL_TEMPLATES

export const TRANSACTIONAL_EMAIL_TEMPLATE_GROUP_ORDER = [
  TRANSACTIONAL_EMAIL_TEMPLATE_GROUP_PASSWORD,
  TRANSACTIONAL_EMAIL_TEMPLATE_GROUP_GIFT,
  TRANSACTIONAL_EMAIL_TEMPLATE_GROUP_COMMERCE,
  TRANSACTIONAL_EMAIL_TEMPLATE_GROUP_SHARE,
]

export type TransactionalEmailFieldInputType =
  | 'text'
  | 'url'
  | 'boolean'
  | 'date'
  | 'select'
  | 'stringArray'
  | 'entitledSkuKeys'
  | 'bonusEligibilityRows'

export interface TransactionalEmailFieldDef {
  key: string
  label: string
  inputType: TransactionalEmailFieldInputType
  required?: boolean
  /** Wire format when inputType is date (default yyyy-MM-dd). */
  dateWireFormat?: BrazeDateWireFormat
  /** Menu options when inputType is select. */
  selectOptions?: readonly BrazeAttributeSuggestedOption[]
}

/** Editable payload fields per template (merged into Brooklyn body) */
export const TRANSACTIONAL_EMAIL_FIELD_DEFS: Record<string, TransactionalEmailFieldDef[]> = {
  PASSWORD_RESET: [
    { key: 'password_reset_url', label: 'Password reset URL', inputType: 'url', required: true },
    registrationLanguageFieldDef('registration_language'),
    { key: 'first_name', label: 'First name', inputType: 'text' },
  ],
  NEW_USER_PASSWORD_RESET: [
    { key: 'password_reset_url', label: 'Password reset URL', inputType: 'url', required: true },
    registrationLanguageFieldDef('registration_language'),
  ],
  LOGIN_CODE: [
    { key: 'magic_link_url', label: 'Magic link URL', inputType: 'url', required: true },
    { key: 'email_mode', label: 'Email mode (code-and-link | link-only)', inputType: 'text' },
    { key: 'web_app_server', label: 'Web app server', inputType: 'url' },
    registrationLanguageFieldDef('registration_language'),
    { key: 'digits_code', label: 'Digits code (required for code-and-link)', inputType: 'text' },
    { key: 'first_name', label: 'First name', inputType: 'text' },
  ],
  GIFT_SUBSCRIPTION_RECIPIENT_DELIVERY: [
    { key: 'gifterName', label: 'Gifter name', inputType: 'text', required: true },
    { key: 'recipientEmail', label: 'Recipient email', inputType: 'text', required: true },
    { key: 'recipientFirstName', label: 'Recipient first name', inputType: 'text' },
    { key: 'recipientLastName', label: 'Recipient last name', inputType: 'text' },
    registrationLanguageFieldDef('registrationLanguage'),
    {
      key: 'deliveryDate',
      label: 'Delivery date',
      inputType: 'date',
      dateWireFormat: 'MM-dd-yyyy',
      required: true,
    },
    {
      key: 'expiresAt',
      label: 'Expires at',
      inputType: 'date',
      dateWireFormat: 'MM-dd-yyyy',
      required: true,
    },
    { key: 'tokenURL', label: 'Token URL', inputType: 'url', required: true },
    { key: 'message', label: 'Message', inputType: 'text' },
    { key: 'planName', label: 'Plan name', inputType: 'text', required: true },
    { key: 'token', label: 'Token', inputType: 'text', required: true },
  ],
  GIFT_SUBSCRIPTION_PURCHASER_RECEIPT: [
    { key: 'first_name', label: 'First name', inputType: 'text' },
    { key: 'last_name', label: 'Last name', inputType: 'text' },
    registrationLanguageFieldDef('registration_language'),
    { key: 'plan_name', label: 'Plan name', inputType: 'text' },
    { key: 'country', label: 'Country', inputType: 'text' },
    { key: 'currency', label: 'Currency', inputType: 'text' },
  ],
  GIFT_SUBSCRIPTION_REDEEMED: [
    { key: 'first_name', label: 'First name', inputType: 'text' },
    registrationLanguageFieldDef('registration_language'),
  ],
  SHARE_VIDEO_LEAD: [
    { key: 'form_name', label: 'Form name', inputType: 'text', required: true },
    registrationLanguageFieldDef('registration_language'),
    { key: 'shared_video_id', label: 'Shared video ID', inputType: 'text', required: true },
    { key: 'new_share_url', label: 'New share URL', inputType: 'url' },
    { key: 'url', label: 'Share URL', inputType: 'url', required: true },
    { key: 'share_watch_link_clicked', label: 'Share watch link clicked', inputType: 'boolean' },
    { key: 'prospect_behavior_segment', label: 'Prospect behavior segment', inputType: 'text' },
    { key: 'prospect_email_comm_br', label: 'Prospect email comm BR', inputType: 'boolean' },
    {
      key: 'everflowTransactionId',
      label: 'Everflow transaction ID (payload root)',
      inputType: 'text',
    },
    { key: 'utm', label: 'UTM query string (payload root)', inputType: 'text' },
  ],
  ALACARTE_PURCHASE_MADE: [
    registrationLanguageFieldDef('registration_language'),
    canvasLanguageFieldDef('Language'),
    { key: 'first_name', label: 'First name', inputType: 'text' },
    { key: 'is_new_user', label: 'Is new user', inputType: 'boolean' },
    { key: 'password_reset_url', label: 'Password reset URL (new users)', inputType: 'url' },
    { key: 'includes_content', label: 'Includes content', inputType: 'boolean' },
    {
      key: 'entitled_ecommerce_sku_keys',
      label: 'Entitled SKU keys',
      inputType: 'entitledSkuKeys',
    },
  ],
  BONUS_GRANTED: [
    registrationLanguageFieldDef('registration_language'),
    canvasLanguageFieldDef('Language'),
    { key: 'plan_name', label: 'Plan name', inputType: 'text' },
    { key: 'first_name', label: 'First name', inputType: 'text' },
    { key: 'includes_content', label: 'Includes content', inputType: 'boolean' },
    { key: 'is_new_user', label: 'Is new user', inputType: 'boolean' },
    {
      key: 'entitled_ecommerce_sku_keys',
      label: 'Entitled SKU keys',
      inputType: 'entitledSkuKeys',
    },
    {
      key: 'ecommerce_sku_mapping_bonus_eligibilities',
      label: 'Bonus eligibilities',
      inputType: 'bonusEligibilityRows',
    },
  ],
  BONUS_SKU_SUBSCRIPTION_SIGNUP: [
    registrationLanguageFieldDef('registration_language'),
    canvasLanguageFieldDef('Language (Canvas locale)'),
    { key: 'plan_name', label: 'Plan name', inputType: 'text' },
    {
      key: 'subscription_tier',
      label: 'Subscription tier (Monthly, Annual, Gaia+, AI Only)',
      inputType: 'text',
    },
    { key: 'trial_period', label: 'Trial period', inputType: 'boolean' },
    { key: 'gaia_product_sku', label: 'Gaia product SKU', inputType: 'text' },
    { key: 'next_bill_amount', label: 'Next bill amount', inputType: 'text' },
    { key: 'paid_through_date', label: 'Paid through date', inputType: 'date' },
    { key: 'currency', label: 'Currency', inputType: 'text' },
  ],
  BONUS_SKU_SUBSCRIPTION_PLAN_CHANGE: [
    registrationLanguageFieldDef('registration_language'),
    canvasLanguageFieldDef('Language (Canvas locale)'),
    { key: 'plan_name', label: 'Plan name', inputType: 'text' },
    {
      key: 'subscription_tier',
      label: 'Subscription tier (Monthly, Annual, Gaia+, AI Only)',
      inputType: 'text',
    },
    { key: 'trial_period', label: 'Trial period', inputType: 'boolean' },
    { key: 'gaia_product_sku', label: 'Gaia product SKU', inputType: 'text' },
    { key: 'next_bill_amount', label: 'Next bill amount', inputType: 'text' },
    { key: 'paid_through_date', label: 'Paid through date', inputType: 'date' },
    { key: 'currency', label: 'Currency', inputType: 'text' },
    { key: 'includes_content', label: 'Includes content', inputType: 'boolean' },
    { key: 'creation_source', label: 'Creation source', inputType: 'text' },
    {
      key: 'entitled_ecommerce_sku_keys',
      label: 'Entitled SKU keys',
      inputType: 'entitledSkuKeys',
    },
    {
      key: 'ecommerce_sku_mapping_bonus_eligibilities',
      label: 'Bonus eligibilities',
      inputType: 'bonusEligibilityRows',
    },
  ],
}

export const CUSTOM_EVENT_FIELD_DEFS = TRANSACTIONAL_EMAIL_FIELD_DEFS
